import { FC, useEffect, useState } from "react";

import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import { AnimatePresence, motion } from "framer-motion";
import { translate } from "lib/blocs/TranslateionBloc";
import { useNavigate, useParams } from "react-router-dom";
import { IconSparks } from "src/constants/icons";
import Path from "src/constants/path";
import HtmlParser from "src/lib/HtmlParser";
import { createAppPath } from "src/lib/createAppPath";
import { fileState, tracker } from "src/state/state";
import Translate from "src/ui/components/Translate/Translate";

import AsyncContent from "../AsyncContent/AsyncContent";
import Feedback from "./Feedback";
import MealInsightBloc from "./MealInsightBloc";
import MealInsightProgress from "./MealInsightProgress";

export const Badge = styled.span`
  background: linear-gradient(
    to right,
    rgba(83, 56, 158, 1),
    rgba(105, 65, 198, 1)
  );
  color: white;
  font-size: 14px;
  font-weight: 500;
  padding: 2px 6px 2px 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  line-height: 140%;
  margin-left: 0.25rem;
  height: 23px;
`;

const Container = styled.div`
  margin: 0 auto;
  min-height: 100vh;
  background: black;
  color: white;
`;

const Gradient = styled.div`
  position: sticky;
  bottom: 0;
  background: linear-gradient(
    180deg,
    rgba(105, 65, 198, 0) 0%,
    rgba(105, 65, 198, 0.4) 100%
  );
  aspect-ratio: 1;
  margin-top: -100%;
  z-index: 0;

  &[data-no-meal="true"] {
    margin-top: 0;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 70px;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0) 0%,
      rgba(26, 21, 41, 0.6) 60%,
      #1a1529 100%
    );
    pointer-events: none;
    z-index: 1;
  }
`;

const FoodImage = styled(motion.div)<{ imageUrl: string }>`
  width: 100%;
  height: 100%;
  background-image: url(${(props) => props.imageUrl});
  background-size: cover;
  background-position: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: max(1rem, env(safe-area-inset-top, 1rem));
  right: max(1rem, env(safe-area-inset-right, 1rem));
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  cursor: pointer;
  z-index: 11;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const ContentSection = styled.div`
  margin-top: -1.5rem;
  margin-bottom: 0.5rem;
  position: relative;
  z-index: 11;
`;

const MealTitle = styled.h1`
  font-weight: 300;
  font-size: 32px;
  line-height: 120%;
`;

const CaloriesContainer = styled.div`
  margin-top: 1rem;
  height: 23px;
  color: white;
  display: flex;
`;

const InsightsSection = styled.div`
  margin-bottom: 1rem;
  padding: 0 1rem;
`;

const InsightsTitle = styled.h3`
  font-family: "STK Bureau";
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  display: flex;
  align-items: top;
`;

const InsightsText = styled.div`
  font-weight: 300;
  font-size: 16px;
  line-height: 140%;

  /* Style all paragraph elements from HTML parser */
  p {
    font-weight: 300 !important;
    font-size: 16px !important;
    line-height: 140% !important;
    margin: 0.5rem 0 !important;
    font-family: "STK Bureau Serif" !important;
  }

  /* Target ALL child elements to force styling */
  * {
    font-weight: 300 !important;
    font-size: 16px !important;
    line-height: 140% !important;
    font-family: "STK Bureau Serif" !important;
  }
`;

const NutritionTable = styled.table`
  width: 100%;
  min-width: 100%;
  max-width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  border-top: 1px solid rgba(230, 227, 219, 0.18);
  border-radius: 0 0 0.5rem 0.5rem;
  box-sizing: border-box;

  tbody tr:first-child th,
  tbody tr:first-child td {
    padding-top: 1rem;
  }
  tbody tr:last-child th,
  tbody tr:last-child td {
    padding-bottom: 1rem;
  }
`;

const NutritionLabel = styled.th`
  text-align: left;
  padding: 0.125rem 1rem 0.125rem 1rem;
  font-size: 1rem;
  font-weight: 500;

  box-sizing: border-box;
`;

const NutritionValueCell = styled.td`
  text-align: right;
  padding: 0.35rem 1rem 0.35rem 0;
  font-size: 1rem;

  box-sizing: border-box;
`;

const Wrapper = styled.div`
  border: 1px solid rgba(230, 227, 219, 0.18);
  border-radius: 0.5rem;
  padding: 1rem 0 0 0;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
`;

const DisclaimerText = styled.p`
  font-size: 0.75rem;
  text-align: left;
  margin: 1rem 0 0 0;
  line-height: 1.4;
  font-weight: 400;
`;

const Blur = styled.div`
  position: relative;
  padding: 0 1rem 4rem;
  z-index: 1;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    pointer-events: none;
    mask-image: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0) 0px,
      rgba(255, 255, 255, 1) 20px,
      rgba(255, 255, 255, 1) 27px,
      rgba(255, 255, 255, 0) 30px
    );
    -webkit-mask-image: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0) 0px,
      rgba(255, 255, 255, 1) 20px,
      rgba(255, 255, 255, 1) 27px,
      rgba(255, 255, 255, 0) 30px
    );
  }
`;

interface NoMealInsightProps {
  imageUrl: string;
  imageLoaded: boolean;
}

const NoMealInsight: FC<NoMealInsightProps> = ({ imageUrl, imageLoaded }) => {
  return (
    <>
      <ImageContainer>
        <CloseButton
          onClick={() => window.history.back()}
          aria-label="Close meal insight"
        >
          ×
        </CloseButton>
        {imageUrl && (
          <FoodImage
            imageUrl={imageUrl}
            role="img"
            aria-label={translate("mealInsights.photoUploaded.alt")}
            initial={{ opacity: 0 }}
            animate={{ opacity: imageLoaded ? 1 : 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        )}
      </ImageContainer>
      <div>
        <Blur>
          <ContentSection>
            <div style={{ height: "29px" }}>
              <MealTitle>
                <Translate msg="mealInsights.notFood.title" />
              </MealTitle>
              <InsightsText>
                <p>
                  <Translate msg="mealInsights.notFood.description" />
                </p>
              </InsightsText>
            </div>
          </ContentSection>
        </Blur>
        <Gradient data-no-meal="true" />
      </div>
    </>
  );
};

const MealInsightOverview: FC = () => {
  const [
    { mealInsight, error, loading, imageError, uploadProgress, previewUrl },
    { generateMealInsight, clearMealInsight }
  ] = useBlocNext(MealInsightBloc);
  const params = useParams();
  const { id } = params;
  const [imageUrl, setImageUrl] = useState<string>("");
  const [imageLoaded, setImageLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      clearMealInsight();
      if (id && id !== Path.tmp) {
        void generateMealInsight(id);
        if (!previewUrl) {
          const maxWidth = 500;
          const windowWidth = window.innerWidth;
          const density = window.devicePixelRatio;
          const width = Math.round(Math.min(windowWidth, maxWidth) * density);
          void fileState.loadPreview(id, width, width).then(setImageUrl);
        }
      }
    }
  }, [id, generateMealInsight, previewUrl]);

  useEffect(() => {
    if (mealInsight?.insights) {
      tracker.track("Meal insight information displayed");
    }
  }, [Boolean(mealInsight?.insights)]);

  useEffect(() => {
    if (imageError || error) {
      navigate(createAppPath(Path.home));
    }
  }, [imageError, error, navigate]);

  // Preload image and track when it's loaded for fade-in animation
  useEffect(() => {
    const displayImageUrl = imageUrl || previewUrl;
    if (displayImageUrl) {
      setImageLoaded(false);
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.src = displayImageUrl;
    }
  }, [imageUrl, previewUrl]);

  const navigateToHome = () => {
    navigate(createAppPath(Path.home));
  };

  const formatCalorieRange = (from?: number, to?: number) => {
    if (from && to) {
      return `${from}-${to} kcal`;
    }
    if (from) {
      return `${from}+ kcal`;
    }
  };

  const formatNutrition = (value?: number, unit: string = "g") => {
    return value ? `${Math.round(value)}${unit}` : "--";
  };

  const isLoading = loading || !mealInsight;
  const insights = mealInsight?.insights;

  const caloryRange = formatCalorieRange(
    insights?.caloriesRange?.from,
    insights?.caloriesRange?.to
  );

  const showMealInsight =
    mealInsight?.insights.mealIdentified ||
    mealInsight?.insights.mealIdentified === undefined;

  const displayImageUrl = imageUrl || previewUrl;

  return (
    <Container>
      {!showMealInsight ? (
        <NoMealInsight
          imageUrl={displayImageUrl ?? ""}
          imageLoaded={imageLoaded}
        />
      ) : (
        <>
          <ImageContainer>
            <CloseButton
              onClick={navigateToHome}
              aria-label={translate("mealInsights.close")}
            >
              ×
            </CloseButton>
            {displayImageUrl && (
              <FoodImage
                imageUrl={displayImageUrl}
                role="img"
                aria-label={translate("mealInsights.mealUploaded.alt")}
                initial={{ opacity: 0 }}
                animate={{ opacity: imageLoaded ? 1 : 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
            )}

            {/* Show progress overlay during upload or analysis */}
            <AnimatePresence>
              {uploadProgress && (
                <MealInsightProgress
                  phase={uploadProgress.phase}
                  percentage={uploadProgress.percentage}
                  loaded={uploadProgress.loaded}
                  total={uploadProgress.total}
                />
              )}
            </AnimatePresence>
          </ImageContainer>

          <div>
            <Blur>
              <ContentSection>
                <AsyncContent check={[!isLoading]} height="29px">
                  <MealTitle>{insights?.mealName}</MealTitle>
                </AsyncContent>

                <AsyncContent check={[!isLoading]} height="29px"></AsyncContent>

                <CaloriesContainer>
                  <p
                    className="as-body2"
                    style={{ marginRight: "0.5rem", marginBottom: "2rem" }}
                  >
                    <Translate msg="mealInsights.estimatedCalories" />
                  </p>
                  <AsyncContent check={[!isLoading]} height="23px" width="54px">
                    <Badge>{caloryRange}</Badge>
                  </AsyncContent>
                </CaloriesContainer>
              </ContentSection>

              <Wrapper>
                <InsightsSection>
                  <InsightsTitle>
                    <IconSparks />
                    <span
                      style={{ marginLeft: "0.5rem", marginBottom: "0.5rem" }}
                    >
                      <Translate msg="mealInsights.insights" />
                    </span>
                  </InsightsTitle>
                  <AsyncContent check={[!isLoading]} height="1rem">
                    <InsightsText>
                      {insights?.insights
                        ? new HtmlParser(insights.insights).toJsx()
                        : null}
                    </InsightsText>
                  </AsyncContent>
                  <AsyncContent check={[!isLoading]} height="1rem" />
                  <AsyncContent check={[!isLoading]} height="1rem" />
                  <AsyncContent
                    check={[!isLoading]}
                    height="1rem"
                    width="90%"
                  />
                  <AsyncContent
                    check={[!isLoading]}
                    height="1rem"
                    width="43%"
                  />
                </InsightsSection>
                <NutritionTable
                  role="table"
                  aria-label="Nutritional information"
                >
                  <thead>
                    <tr>
                      <NutritionLabel className="only-sr" scope="col">
                        <Translate msg="mealInsights.nutrition.nutrient" />
                      </NutritionLabel>
                      <NutritionLabel className="only-sr" scope="col">
                        <Translate msg="mealInsights.nutrition.amount" />
                      </NutritionLabel>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <NutritionLabel scope="row">
                        <Translate msg="mealInsights.nutrition.protein" />
                      </NutritionLabel>
                      <NutritionValueCell>
                        <AsyncContent
                          check={[!isLoading]}
                          height="1rem"
                          width="2rem"
                        >
                          <p className="as-body2">
                            {formatNutrition(insights?.protein)}
                          </p>
                        </AsyncContent>
                      </NutritionValueCell>
                    </tr>

                    <tr>
                      <NutritionLabel scope="row">
                        <Translate msg="mealInsights.nutrition.fiber" />
                      </NutritionLabel>
                      <NutritionValueCell>
                        <AsyncContent
                          check={[!isLoading]}
                          height="1rem"
                          width="2rem"
                        >
                          <p className="as-body2">
                            {formatNutrition(insights?.fiber)}
                          </p>
                        </AsyncContent>
                      </NutritionValueCell>
                    </tr>

                    <tr>
                      <NutritionLabel scope="row">
                        <Translate msg="mealInsights.nutrition.totalFats" />
                      </NutritionLabel>
                      <NutritionValueCell>
                        <AsyncContent
                          check={[!isLoading]}
                          height="1rem"
                          width="2rem"
                        >
                          <p className="as-body2">
                            {formatNutrition(insights?.totalFats)}
                          </p>
                        </AsyncContent>
                      </NutritionValueCell>
                    </tr>

                    <tr>
                      <NutritionLabel scope="row">
                        <Translate msg="mealInsights.nutrition.totalCarbs" />
                      </NutritionLabel>
                      <NutritionValueCell>
                        <AsyncContent
                          check={[!isLoading]}
                          height="1rem"
                          width="2rem"
                        >
                          <p className="as-body2">
                            {formatNutrition(insights?.totalCarbohydrates)}
                          </p>
                        </AsyncContent>
                      </NutritionValueCell>
                    </tr>
                  </tbody>
                </NutritionTable>
              </Wrapper>
              {!isLoading && mealInsight && <Feedback />}
              <DisclaimerText>
                <Translate msg="mealInsights.feedback.disclaimer" />
              </DisclaimerText>
            </Blur>
            <Gradient />
          </div>
        </>
      )}
    </Container>
  );
};

export default MealInsightOverview;
